import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import api from '../services/api';
import { SPORT_LABELS, SPORT_ICONS, getFacilityThumb } from '../config/venueConfig';

const NAVY = '#0A2C93';
const ORANGE = '#F97316';

const BookPage = () => {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [expanded, setExpanded] = useState(null); // venueId whose sport panel is open
  const [selectedSport, setSelectedSport] = useState(null);
  const [prices, setPrices] = useState({}); // { sport: minPerSlotPrice } for the expanded venue
  const [pricesLoading, setPricesLoading] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    api.getPublicFacilities().then((f) => setFacilities(f || [])).catch(() => {});
  }, []);

  const rawSports = (f) => f.sports || [];

  // Fetch today's availability once for the expanded venue → min per-30-min price per sport.
  const loadPrices = async (venueId, sports) => {
    setPricesLoading(true);
    setPrices({});
    try {
      const today = new Date().toISOString().slice(0, 10);
      const avail = await api.getPublicSlotAvailability(today, venueId);
      const courtSport = {};
      (avail?.courts || []).forEach((c) => { courtSport[c.id] = c.sport; });
      const min = {};
      Object.values(avail?.slots || {}).forEach((slot) => {
        Object.entries(slot?.sections || {}).forEach(([courtId, sec]) => {
          const sp = sec.sport || courtSport[courtId];
          if (!sp || !sec.price) return;
          const perSlot = sec.price / 2; // section price is hourly; grid shows per-30-min
          if (min[sp] == null || perSlot < min[sp]) min[sp] = perSlot;
        });
      });
      setPrices(min);
    } catch (_) {
      setPrices({});
    } finally {
      setPricesLoading(false);
    }
  };

  const handleVenueTap = (f) => {
    const sports = rawSports(f);
    if (sports.length <= 1) {
      navigate(`/book/${f.id}`); // single-sport → straight to grid (unchanged behaviour)
      return;
    }
    if (expanded === f.id) {
      setExpanded(null); setSelectedSport(null); setPrices({});
      return;
    }
    setExpanded(f.id);
    setSelectedSport(null);
    loadPrices(f.id, sports);
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-12">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1" data-testid="book-back-btn">
            <ChevronLeft className="w-5 h-5" style={{ color: NAVY }} />
          </button>
          <h1 className="font-heading font-bold text-lg" style={{ color: NAVY }}>Book a Venue</h1>
        </div>
      </header>

      <section className="max-w-2xl mx-auto px-4 py-5" data-testid="select-venue-section">
        <h2 className="font-heading font-bold text-base mb-1" style={{ color: NAVY }}>Choose a venue</h2>
        <p className="text-xs text-slate-500 mb-4">Multi-sport venues let you pick a sport — tap to choose.</p>

        <div className="space-y-3">
          {facilities.map((f) => {
            const img = getFacilityThumb(f.id);
            const sports = rawSports(f);
            const isMulti = sports.length > 1;
            const isOpen = expanded === f.id;
            return (
              <div key={f.id} ref={isOpen ? panelRef : undefined}
                className={`rounded-2xl border-2 overflow-hidden transition-colors ${isOpen ? 'bg-white' : 'border-slate-100 bg-white'}`}
                style={isOpen ? { borderColor: NAVY } : {}}>
                {/* Venue card */}
                <button onClick={() => handleVenueTap(f)} data-testid={`venue-select-${f.id}`}
                  className="w-full flex items-center gap-3 p-3 text-left">
                  <img src={img} alt={f.name} loading="lazy" decoding="async" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-sm truncate" style={{ color: NAVY }}>{f.name}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {sports.map((s) => (
                        <span key={s} className="text-[10px] font-heading font-semibold text-slate-500">{SPORT_LABELS[s] || s}</span>
                      ))}
                    </div>
                    {isMulti && (
                      <span className="inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${ORANGE}1A`, color: ORANGE }} data-testid={`venue-multisport-tag-${f.id}`}>
                        {sports.length} sports · tap to choose
                      </span>
                    )}
                  </div>
                  {isMulti
                    ? <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    : <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />}
                </button>

                {/* Inline sport panel — connected to the card above */}
                {isOpen && isMulti && (
                  <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid={`venue-sports-panel-${f.id}`}>
                    <p className="text-[11px] font-semibold text-slate-500 mb-2 mt-2">Choose a sport at {f.name}</p>
                    <div className="grid grid-cols-2 gap-2.5">
                      {sports.map((s) => {
                        const sel = selectedSport === s;
                        const p = prices[s];
                        return (
                          <button key={s} onClick={() => setSelectedSport(s)} data-testid={`sport-select-${s}`}
                            className={`flex flex-col items-start gap-1 p-3 rounded-xl border-2 transition-colors ${sel ? '' : 'border-slate-100 bg-white'}`}
                            style={sel ? { borderColor: NAVY, background: `${NAVY}0D` } : {}}>
                            <span className="text-2xl leading-none">{SPORT_ICONS[s] || '🏅'}</span>
                            <span className="font-heading font-bold text-sm" style={{ color: NAVY }}>{SPORT_LABELS[s] || s}</span>
                            <span className="text-[11px] font-semibold text-slate-500 min-h-[14px]">
                              {pricesLoading ? '…' : (p != null ? `from ₹${Math.round(p).toLocaleString('en-IN')}` : '')}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Inline continue — appears once a sport is picked; never covers the options */}
                    {selectedSport && (
                      <button onClick={() => navigate(`/book/${f.id}?sport=${selectedSport}`)} data-testid="book-continue-btn"
                        className="w-full mt-3 py-3 rounded-full font-heading font-semibold text-sm text-white flex items-center justify-center gap-1"
                        style={{ background: ORANGE }}>
                        Continue with {SPORT_LABELS[selectedSport] || selectedSport} <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {facilities.length === 0 && (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookPage;
